import { Head, Link } from "@inertiajs/react"
import BadgeIcon from "@mui/icons-material/Badge"
import BackupTableIcon from "@mui/icons-material/BackupTable"
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange"
import DateRangeIcon from "@mui/icons-material/DateRange"
import HistoryIcon from "@mui/icons-material/History"
import ListAltIcon from "@mui/icons-material/ListAlt"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing"
import {
    alpha,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Container,
    SvgIconProps,
    Typography,
} from "@mui/material"
import { ElementType } from "react"
import { useTranslation } from "react-i18next"

interface Module {
    titleKey: string
    descKey: string
    icon: ElementType<SvgIconProps>
    href: string
    isNew?: boolean
    disabled?: boolean
}

const HHRR_MODULES: Module[] = [
    { titleKey: "modules.employees",    descKey: "modules.employeesDesc",   icon: BadgeIcon,           href: "/employees" },
    { titleKey: "modules.generalInfo",  descKey: "modules.generalInfoDesc", icon: BackupTableIcon,     href: "/general_infos" },
    { titleKey: "modules.weeklyReport", descKey: "modules.weeklyReportDesc",icon: DateRangeIcon,       href: "/weekly_reports" },
    { titleKey: "modules.payrolls",     descKey: "modules.payrollsDesc",    icon: CurrencyExchangeIcon,href: "/payrolls" },
]

const PRODUCTION_MODULES: Module[] = [
    { titleKey: "modules.orders",    descKey: "modules.ordersDesc",    icon: ListAltIcon,                href: "/orders" },
    { titleKey: "modules.extrusion", descKey: "modules.extrusionDesc", icon: PrecisionManufacturingIcon, href: "/extrusions", isNew: true },
    { titleKey: "modules.exports",   descKey: "modules.exportsDesc",   icon: LocalShippingIcon,          href: "/exports",   isNew: true },
]

const SYSTEM_MODULES: Module[] = [
    { titleKey: "modules.history", descKey: "modules.historyDesc", icon: HistoryIcon, href: "/history", isNew: true },
]

function ModuleCard({ titleKey, descKey, icon: Icon, href, isNew, disabled }: Module) {
    const { t } = useTranslation()
    return (
        <Card
            variant="outlined"
            sx={{
                height: "100%",
                transition: "box-shadow 0.2s, transform 0.2s",
                ...(!disabled && {
                    "&:hover": {
                        boxShadow: 6,
                        transform: "translateY(-2px)",
                    },
                }),
                ...(disabled && { opacity: 0.45 }),
            }}
        >
            <CardActionArea
                component={!disabled ? Link : "div"}
                href={!disabled ? href : undefined}
                disabled={disabled}
                sx={{ height: "100%", alignItems: "flex-start", cursor: disabled ? "default" : undefined }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                                color: "primary.main",
                            }}
                        >
                            <Icon fontSize="medium" />
                        </Box>
                        {isNew && (
                            <Chip label={t("home.isNew")} size="small" color="primary" variant="outlined" />
                        )}
                    </Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
                        {t(titleKey)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t(descKey)}
                    </Typography>
                </CardContent>
            </CardActionArea>
        </Card>
    )
}

function Section({ titleKey, modules }: { titleKey: string; modules: Module[] }) {
    const { t } = useTranslation()
    return (
        <Box sx={{ mb: 5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: "bold", letterSpacing: 1.5 }}>
                {t(titleKey)}
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: 2,
                    mt: 1.5,
                }}
            >
                {modules.map(mod => (
                    <ModuleCard key={mod.titleKey} {...mod} />
                ))}
            </Box>
        </Box>
    )
}

export default function HomeIndex() {
    const { t } = useTranslation()
    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Head title={t("home.title")} />
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                {t("home.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                {t("home.subtitle")}
            </Typography>
            <Section titleKey="home.humanResources" modules={HHRR_MODULES} />
            <Section titleKey="home.production" modules={PRODUCTION_MODULES} />
            <Section titleKey="home.system" modules={SYSTEM_MODULES} />
        </Container>
    )
}
